import { T, useGT } from 'gt-react';
import { Label } from 'src/components/ui/label';
import { SingleSelect } from 'src/components/common/SingleSelect';
import {
  placementFromOptionId,
  placementOptionId,
  type PagePlacement,
  type PagePlacementOption,
} from './pagePlacement';

/**
 * The "Sidebar section" picker shared by the task page and agent page
 * editors: one flat list of the org's own page sections plus the built-in
 * route-derived sections. Clearing it sends the page to the generic "Pages"
 * fallback group.
 */
export function PagePlacementField({
  id,
  placement,
  options,
  onChange,
}: {
  id: string;
  placement: PagePlacement;
  options: PagePlacementOption[];
  onChange: (placement: PagePlacement) => void;
}) {
  const gt = useGT();
  const selectedId = placementOptionId(placement);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        <T context="form field label, which sidebar section a page appears in">
          Sidebar section
        </T>
      </Label>
      <SingleSelect
        id={id}
        options={options}
        value={options.find((o) => o.id === selectedId) ?? null}
        onChange={(o) => onChange(placementFromOptionId(o?.id))}
        placeholder={gt('Pages (default section)', {
          context:
            'sidebar section picker placeholder in the page editor, shown when the page goes to the generic Pages section',
        })}
        shouldAutoSort={false}
        allowClear
        fullWidth
      />
      <p className="text-[11px] text-muted-foreground">
        <T context="hint under the page editor sidebar section field">
          Pick one of your own sections or a built-in one. Create sections under
          Settings → Page Sections.
        </T>
      </p>
    </div>
  );
}
